import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Heading,
  Spinner,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  List,
  ListItem,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  Checkbox,
  Button,
  HStack,
} from "@chakra-ui/react";
import { getConnectionMetadata, updateConnection, importConnectionMetadata } from "../../services/IntegrationService";
import { DataWarehouseType, DataWarehouseObjectType } from "build-ts/thoughtspot";
import showToast from "hooks/useCustomToast";

interface MetadataExplorerProps {
  connectionId: string;
  dataWarehouseType: DataWarehouseType;
}

interface MetadataNode {
  id: string;
  name: string;
  type: string;
  children?: MetadataNode[];
  metadata?: Record<string, any>;
}

interface SelectedColumn {
  databaseName: string;
  schemaName: string;
  tableName: string;
  columnName: string;
  dataType: string;
  canImport: boolean;
  isLinkedActive: boolean;
  isAggregate: boolean | string;
  description: string;
  tableType: string;
  tableDescription: string;
  tableLinked: boolean;
  tableSelected: boolean;
  databaseAutoCreated: boolean;
}

const MetadataExplorer: React.FC<MetadataExplorerProps> = ({ connectionId, dataWarehouseType }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [databases, setDatabases] = useState<MetadataNode[]>([]);
  const [selectedDatabase, setSelectedDatabase] = useState<string | null>(null);
  const [selectedSchema, setSelectedSchema] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [metadataCache, setMetadataCache] = useState<any>(null);
  const [selectedColumns, setSelectedColumns] = useState<MetadataNode[]>([]);
  const [selectedColumnMap, setSelectedColumnMap] = useState<Map<string, SelectedColumn>>(new Map());
  const navigate = useNavigate();

  useEffect(() => {
    fetchMetadata();
  }, [connectionId, dataWarehouseType]);

  const fetchMetadata = async () => {
    setLoading(true);
    setError(null);
  
    const response = await getConnectionMetadata(
      connectionId,
      dataWarehouseType,
      DataWarehouseObjectType.COLUMN,
      true
    );
    console.log("Metadata API Response:", response);
    
    if (response && response.data_warehouse_objects) {
      setMetadataCache(response.data_warehouse_objects);
      const dbList = (response.data_warehouse_objects.databases || []).map((db: any) => ({
        id: db.name,
        name: db.name,
        type: "database",
        metadata: { auto_created: db.auto_created }
      }));
      setDatabases(dbList);
    } else {
      console.log("No data_warehouse_objects found in response");
      setDatabases([]);
    }
    setLoading(false);
  };

  const handleDatabaseClick = (dbId: string) => {
    setSelectedDatabase(dbId);
    setSelectedSchema(null);
    setSelectedTable(null);
    setSelectedColumns([]);
    
    const db = databases.find(d => d.id === dbId);
    if (db && !db.children && metadataCache) {
      const dbData = metadataCache.databases.find((d: any) => d.name === dbId);
      const schemas = (dbData?.schemas || []).map((schema: any) => ({
        id: schema.name,
        name: schema.name,
        type: "schema"
      }));
      setDatabases(prev => prev.map(d => 
        d.id === dbId ? { ...d, children: schemas } : d
      ));
    }
  };

  const handleSchemaClick = (dbId: string, schemaId: string) => {
    setSelectedSchema(schemaId);
    setSelectedTable(null);
    setSelectedColumns([]);
    
    const db = databases.find(d => d.id === dbId);
    const schema = db?.children?.find(s => s.id === schemaId);
    if (schema && !schema.children && metadataCache) {
      const dbData = metadataCache.databases.find((d: any) => d.name === dbId);
      const schemaData = dbData?.schemas.find((s: any) => s.name === schemaId);
      const tables = (schemaData?.tables || []).map((table: any) => ({
        id: table.name,
        name: table.name,
        type: "table",
        metadata: { description: table.description }
      }));
      setDatabases(prev => prev.map(d => 
        d.id === dbId ? {
          ...d,
          children: d.children?.map(s =>
            s.id === schemaId ? { ...s, children: tables } : s
          )
        } : d
      ));
    }
  };

  const handleTableClick = (dbId: string, schemaId: string, tableId: string) => {
    setSelectedTable(tableId);
    
    const db = databases.find(d => d.id === dbId);
    const schema = db?.children?.find(s => s.id === schemaId);
    const table = schema?.children?.find(t => t.id === tableId);
    if (table && metadataCache) {
      const dbData = metadataCache.databases.find((d: any) => d.name === dbId);
      const schemaData = dbData?.schemas.find((s: any) => s.name === schemaId);
      const tableData = schemaData?.tables.find((t: any) => t.name === tableId);
      const columns = (tableData?.columns || []).map((column: any) => ({
        id: column.name,
        name: column.name,
        type: "column",
        metadata: {
          dataType: column.data_type,
        }
      }));
      setSelectedColumns(columns);
    }
  };

  const handleColumnSelect = (column: MetadataNode, isSelected: boolean) => {
    if (!selectedDatabase || !selectedSchema || !selectedTable) return;

    const columnKey = `${selectedDatabase}.${selectedSchema}.${selectedTable}.${column.name}`;
    
    if (isSelected) {
      const dbData = metadataCache.databases.find((d: any) => d.name === selectedDatabase);
      const schemaData = dbData?.schemas.find((s: any) => s.name === selectedSchema);
      const tableData = schemaData?.tables.find((t: any) => t.name === selectedTable);
      const columnData = tableData?.columns.find((c: any) => c.name === column.name);

      setSelectedColumnMap(prev => {
        const newMap = new Map(prev);
        newMap.set(columnKey, {
          databaseName: selectedDatabase,
          schemaName: selectedSchema,
          tableName: selectedTable,
          columnName: column.name,
          dataType: column.metadata?.dataType,
          canImport: columnData?.can_import || true,
          isLinkedActive: columnData?.is_linked_active || true,
          isAggregate: columnData?.is_aggregate || false,
          description: columnData?.description || "",
          tableType: tableData?.type || "TABLE",
          tableDescription: tableData?.description || "",
          tableLinked: tableData?.linked || true,
          tableSelected: tableData?.selected || true,
          databaseAutoCreated: dbData?.auto_created || false
        });
        return newMap;
      });
    } else {
      setSelectedColumnMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(columnKey);
        return newMap;
      });
    }
  };

  const isColumnSelected = (column: MetadataNode): boolean => {
    if (!selectedDatabase || !selectedSchema || !selectedTable) return false;
    const columnKey = `${selectedDatabase}.${selectedSchema}.${selectedTable}.${column.name}`;
    return selectedColumnMap.has(columnKey);
  };

  const handleSelectAllColumns = (isSelected: boolean) => {
    if (!selectedDatabase || !selectedSchema || !selectedTable) return;

    if (isSelected) {
      const dbData = metadataCache.databases.find((d: any) => d.name === selectedDatabase);
      const schemaData = dbData?.schemas.find((s: any) => s.name === selectedSchema);
      const tableData = schemaData?.tables.find((t: any) => t.name === selectedTable);

      const newSelections = new Map(selectedColumnMap);
      selectedColumns.forEach(column => {
        const columnKey = `${selectedDatabase}.${selectedSchema}.${selectedTable}.${column.name}`;
        const columnData = tableData?.columns.find((c: any) => c.name === column.name);

        newSelections.set(columnKey, {
          databaseName: selectedDatabase,
          schemaName: selectedSchema,
          tableName: selectedTable,
          columnName: column.name,
          dataType: column.metadata?.dataType,
          canImport: columnData?.can_import || true,
          isLinkedActive: columnData?.is_linked_active || true,
          isAggregate: columnData?.is_aggregate || false,
          description: columnData?.description || "",
          tableType: tableData?.type || "TABLE",
          tableDescription: tableData?.description || "",
          tableLinked: tableData?.linked || true,
          tableSelected: tableData?.selected || true,
          databaseAutoCreated: dbData?.auto_created || false
        });
      });
      setSelectedColumnMap(newSelections);
    } else {
      const newSelections = new Map(selectedColumnMap);
      selectedColumns.forEach(column => {
        const columnKey = `${selectedDatabase}.${selectedSchema}.${selectedTable}.${column.name}`;
        newSelections.delete(columnKey);
      });
      setSelectedColumnMap(newSelections);
    }
  };

  const areAllColumnsSelected = (): boolean => {
    if (!selectedDatabase || !selectedSchema || !selectedTable || selectedColumns.length === 0) return false;
    
    return selectedColumns.every(column => {
      const columnKey = `${selectedDatabase}.${selectedSchema}.${selectedTable}.${column.name}`;
      return selectedColumnMap.has(columnKey);
    });
  };

  const handleUpdateConnection = async () => {
    if (selectedColumnMap.size === 0) {
      showToast("No columns selected", "Please select at least one column to update the connection.", "warning");
      return;
    }

    // Create a Set of selected items for faster lookup
    const selectedDatabases = new Set<string>();
    const selectedSchemas = new Set<string>();
    const selectedTables = new Set<string>();
    const selectedColumns = new Set<string>();

    selectedColumnMap.forEach((selection) => {
      selectedDatabases.add(selection.databaseName);
      selectedSchemas.add(`${selection.databaseName}.${selection.schemaName}`);
      selectedTables.add(`${selection.databaseName}.${selection.schemaName}.${selection.tableName}`);
      selectedColumns.add(`${selection.databaseName}.${selection.schemaName}.${selection.tableName}.${selection.columnName}`);
    });

    // Filter and preserve the original metadata structure
    const externalDatabases = metadataCache.databases
      .filter((db: any) => selectedDatabases.has(db.name))
      .map((db: any) => {
        // Keep all original database properties and convert to camelCase
        const filteredDb = { ...db };
        if ("auto_created" in filteredDb) {
          filteredDb.isAutoCreated = filteredDb.auto_created;
          delete filteredDb.auto_created;
        }
        
        // Filter schemas
        filteredDb.schemas = db.schemas
          .filter((schema: any) => selectedSchemas.has(`${db.name}.${schema.name}`))
          .map((schema: any) => {
            // Keep all original schema properties
            const filteredSchema = { ...schema };
            
            // Filter tables
            filteredSchema.tables = schema.tables
              .filter((table: any) => selectedTables.has(`${db.name}.${schema.name}.${table.name}`))
              .map((table: any) => {
                // Keep all original table properties and convert to camelCase
                const filteredTable = { ...table };
                filteredTable.selected = true;
                if ("data_type" in filteredTable) {
                  filteredTable.dataType = filteredTable.data_type;
                  delete filteredTable.data_type;
                }
                if ("dataset_id" in filteredTable) {
                  filteredTable.datasetId = filteredTable.dataset_id;
                  delete filteredTable.dataset_id;
                }
                if ("sub_type" in filteredTable) {
                  filteredTable.subType = filteredTable.sub_type;
                  delete filteredTable.sub_type;
                }
                
                // Filter columns
                filteredTable.columns = table.columns
                  .filter((column: any) => selectedColumns.has(`${db.name}.${schema.name}.${table.name}.${column.name}`))
                  .map((column: any) => {
                    // Convert column properties to camelCase
                    const camelCaseColumn = {
                      ...column,
                      selected: true,
                      type: column.data_type || column.type,
                      canImport: column.can_import,
                      isLinkedActive: column.is_linked_active,
                      isAggregate: column.is_aggregate
                    };
                    
                    // Remove snake_case properties
                    delete camelCaseColumn.data_type;
                    delete camelCaseColumn.can_import;
                    delete camelCaseColumn.is_linked_active;
                    delete camelCaseColumn.is_aggregate;
                    
                    return camelCaseColumn;
                  });
                
                return filteredTable;
              });
            
            return filteredSchema;
          });
        
        return filteredDb;
      });

    setLoading(true);
    
    // Update connection
    await updateConnection(connectionId, {
      data_warehouse_config: JSON.stringify({
        authenticationType: "SERVICE_ACCOUNT",
        externalDatabases: externalDatabases,
        configuration: {}
      }),
      validate: true
    });
    // Import metadata
    const importResponse = await importConnectionMetadata(connectionId);
    if (!importResponse) {
      showToast("Failed to import connection metadata", "", "error");
      return;
    }
    showToast("Connection updated", "Successfully updated the connection and imported metadata.", "success");
    // Redirect to metric page
    navigate("/metric");
    setLoading(false);
  };

  if (loading && databases.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Spinner />
        <Text mt={2}>Loading metadata...</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} color="red.500">
        <Text>{error}</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Grid templateColumns="minmax(300px, 1fr) minmax(400px, 2fr)" gap={6}>
        {/* Left Panel - Database Explorer */}
        <GridItem>
          <Card>
            <CardHeader>
              <Heading size="md">Database Explorer</Heading>
            </CardHeader>
            <CardBody p={2}>
              <Accordion allowMultiple>
                {databases.map(db => (
                  <AccordionItem key={db.id}>
                    <AccordionButton 
                      onClick={() => handleDatabaseClick(db.id)}
                      bg={selectedDatabase === db.id ? "blue.50" : "transparent"}
                    >
                      <Box flex="1" textAlign="left">
                        <Text fontWeight="bold">{db.name}</Text>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={2} px={2}>
                      {db?.children && (
                        <Accordion allowMultiple>
                          {db?.children?.map(schema => (
                            <AccordionItem key={schema.id} border="none">
                              <AccordionButton 
                                onClick={() => handleSchemaClick(db.id, schema.id)}
                                bg={selectedSchema === schema.id ? "blue.50" : "transparent"}
                                _hover={{ bg: "gray.50" }}
                              >
                                <Box flex="1" textAlign="left" pl={2}>
                                  <Text>{schema.name}</Text>
                                </Box>
                                <AccordionIcon />
                              </AccordionButton>
                              <AccordionPanel pb={2} px={2}>
                                {schema?.children && (
                                  <List spacing={1}>
                                    {schema?.children?.map(table => (
                                      <ListItem
                                        key={table.id}
                                        cursor="pointer"
                                        p={2}
                                        pl={4}
                                        borderRadius="md"
                                        bg={selectedTable === table.id ? "blue.50" : "transparent"}
                                        _hover={{ bg: "gray.50" }}
                                        onClick={() => handleTableClick(db.id, schema.id, table.id)}
                                      >
                                        <Text fontSize="sm">{table.name}</Text>
                                      </ListItem>
                                    ))}
                                  </List>
                                )}
                              </AccordionPanel>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      )}
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardBody>
          </Card>
        </GridItem>

        {/* Right Panel - Column Details */}
        <GridItem>
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">
                  {selectedTable ? `Columns: ${selectedTable}` : "Select a table to view columns"}
                </Heading>
                {selectedColumns.length > 0 && (
                  <Checkbox
                    isChecked={areAllColumnsSelected()}
                    onChange={(e) => handleSelectAllColumns(e.target.checked)}
                    colorScheme="blue"
                  >
                    Select All
                  </Checkbox>
                )}
              </HStack>
            </CardHeader>
            <CardBody>
              {selectedColumns.length > 0 ? (
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th width="50px"></Th>
                      <Th>Column Name</Th>
                      <Th>Type</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {selectedColumns.map(column => (
                      <Tr key={column.id}>
                        <Td>
                          <Checkbox
                            isChecked={isColumnSelected(column)}
                            onChange={(e) => handleColumnSelect(column, e.target.checked)}
                            colorScheme="blue"
                            size="lg"
                          />
                        </Td>
                        <Td>{column.name}</Td>
                        <Td>{column.metadata?.dataType}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Box textAlign="center" py={8} color="gray.500">
                  <Text>Select a table from the left panel to view its columns</Text>
                </Box>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </Grid>

      {/* Update Button */}
      <Box mt={4} textAlign="right">
        <Button
          colorScheme="blue"
          onClick={handleUpdateConnection}
          isLoading={loading}
          isDisabled={selectedColumnMap.size === 0}
        >
          Update Connection ({selectedColumnMap.size} columns selected)
        </Button>
      </Box>
    </Box>
  );
};

export default MetadataExplorer; 